import { usePagination } from 'utils/usePagination'
import * as S from './styles'
import type { PaginationSize } from './styles'
import { ITablePaginationOptions } from '../types'
import { useDataTableContext } from '../DataTableContext'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'
import FbButton from 'components/FbUI/FbButton'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { useToolbarDensity } from 'components/Tables/CommonTableCells/ToolbarDensityContext'

export interface ITablePaginationProps {
  paginationOptions?: ITablePaginationOptions
  /** Visual density override. When omitted, the size is derived from the
   *  surrounding `ToolbarDensityContext`: a `sm`/`xs` toolbar gets the compact
   *  32px / 12px-font row, everything else keeps the 40px (`md`) row. */
  size?: PaginationSize
}

export default function DataTablePagination({
  paginationOptions = {
    page: 1,
    totalRows: 0,
    pageSize: 100,
    setPage: () => {},
    isPaginationLoading: false,
  },
  size,
}: ITablePaginationProps) {
  // Derive the footer density from the toolbar density so only tables that opt
  // into a compact toolbar (ToolbarDensityProvider sm/xs) get compact
  // pagination; tables with no provider keep the legacy 40px row. An explicit
  // `size` prop still wins.
  const density = useToolbarDensity()
  const resolvedSize: PaginationSize = size ?? (density === 'md' ? 'md' : 'sm')
  const chevronSize = resolvedSize === 'sm' ? 18 : 20
  const pageCount = Math.ceil(
    paginationOptions.totalRows / paginationOptions.pageSize
  )
  const currentPage = paginationOptions.page ?? 1
  const canPreviousPage = currentPage > 1
  const canNextPage = currentPage < pageCount

  function nextPage() {
    paginationOptions.setPage?.(currentPage + 1)
  }

  function prevPage() {
    paginationOptions.setPage?.(currentPage - 1)
  }

  // Below the xl breakpoint the right side of the table chrome (Next +
  // Add/Download buttons) starts to overlap the page numbers. Drop the
  // sibling count to 0 so the middle range shrinks from
  // `… current-1 current current+1 …` to just `… current …`.
  const isNarrow = useMediaQuery('(max-width: 1279px)')
  const paginationRange = usePagination({
    siblingCount: isNarrow ? 0 : 1,
    currentPage: currentPage,
    pageCount: pageCount,
  })

  const {
    methods: { clearSelectedRows },
  } = useDataTableContext()

  return (
    <S.DataTablePaginationContainer $size={resolvedSize}>
      <FbButton
        variant="secondary"
        size={resolvedSize}
        aria-label="Previous page"
        className="!border-neutral-300 !text-neutral-800 hover:!bg-neutral-100 hover:!text-neutral-900"
        onClick={() => {
          if (canPreviousPage) {
            clearSelectedRows()
            prevPage()
          }
        }}
        style={{
          visibility: canPreviousPage ? 'visible' : 'hidden',
        }}
        disabled={paginationOptions.isPaginationLoading}
      >
        <MdChevronLeft size={chevronSize} />
        <span className="hidden lg:inline">Previous</span>
      </FbButton>
      <S.DataTablePaginationButtonsContainer>
        {paginationRange?.map((pageNumber, index) => {
          if (pageNumber === '...') {
            return (
              <S.DataTablePaginationDots key={index}>
                <span>. . .</span>
              </S.DataTablePaginationDots>
            )
          }

          return (
            <S.PageButton
              isActive={pageNumber === currentPage}
              $size={resolvedSize}
              key={'page-' + pageNumber}
              onClick={() => {
                clearSelectedRows()
                paginationOptions.setPage?.(pageNumber as number)
              }}
            >
              {pageNumber}
            </S.PageButton>
          )
        })}
      </S.DataTablePaginationButtonsContainer>
      <FbButton
        variant="secondary"
        size={resolvedSize}
        aria-label="Next page"
        className="!border-neutral-300 !text-neutral-800 hover:!bg-neutral-100 hover:!text-neutral-900"
        onClick={() => {
          if (canNextPage) {
            clearSelectedRows()
            nextPage()
          }
        }}
        style={{
          visibility: canNextPage ? 'visible' : 'hidden',
        }}
        disabled={paginationOptions.isPaginationLoading}
      >
        <span className="hidden lg:inline">Next</span>
        <MdChevronRight size={chevronSize} />
      </FbButton>
    </S.DataTablePaginationContainer>
  )
}
